import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { PieChart } from 'react-native-chart-kit';

const RetirementCalculatorScreen = () => {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isMobile = screenData.width < 900;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  const [formData, setFormData] = useState({
    monthlyContribution: '10000',
    expectedReturn: '12',
    yearsUntilRetirement: '30',
    currentSavings: '0',
    annuityPurchasePercent: '40',
    annuityReturnRate: '6',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateRetirement = () => {
    const Pm = parseFloat(formData.monthlyContribution);
    const annualReturn = parseFloat(formData.expectedReturn);
    const years = parseFloat(formData.yearsUntilRetirement);
    const currentSavings = parseFloat(formData.currentSavings);
    const annuityPct = parseFloat(formData.annuityPurchasePercent);
    const annuityRatePA = parseFloat(formData.annuityReturnRate);

    if ([Pm, annualReturn, years, currentSavings, annuityPct, annuityRatePA].some(v => isNaN(v))) {
      setResults(null);
      return;
    }

    const r = annualReturn / 12 / 100;
    const n = years * 12;

    // FV of monthly SIP
    const corpusFromSIP = Pm * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
    // FV of current savings (lumpsum)
    const corpusFromSavings = currentSavings * Math.pow(1 + r, n);
    const totalCorpus = corpusFromSIP + corpusFromSavings;

    const annuityAmount = totalCorpus * (annuityPct / 100);
    const lumpSum = totalCorpus - annuityAmount;
    const monthlyPension = annuityAmount * (annuityRatePA / 12 / 100);

    const investedAmount = (Pm * n) + currentSavings;
    const expectedReturns = totalCorpus - investedAmount;

    setResults({
      retirementCorpus: Math.round(totalCorpus),
      investedAmount: Math.round(investedAmount),
      expectedReturns: Math.round(expectedReturns),
      annuityPurchase: Math.round(annuityAmount),
      lumpSumWithdrawal: Math.round(lumpSum),
      estimatedMonthlyPension: Math.round(monthlyPension),
      monthlyContribution: Pm,
      expectedReturn: annualReturn,
      yearsUntilRetirement: years,
      currentSavings,
      annuityPurchasePercent: annuityPct,
      annuityReturnRate: annuityRatePA,
    });
  };

  useEffect(() => {
    calculateRetirement();
  }, [formData]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>Retirement Calculator</Text>
        <Text style={styles.heroSubtitle}>
          Estimate retirement corpus, lump-sum withdrawal and monthly pension
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
        <View style={[styles.formContent, isMobile && { flex: 1 }]}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly Contribution</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput
                style={styles.input}
                value={formData.monthlyContribution}
                onChangeText={(v) => handleInputChange('monthlyContribution', v)}
                keyboardType="numeric"
                placeholder="10000"
              />
            </View>
          </View>

          <View style={[styles.inputRow, isMobile && { flexDirection: 'column', gap: 0 }]}>
            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Expected Return (p.a.)</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.expectedReturn}
                  onChangeText={(v) => handleInputChange('expectedReturn', v)}
                  keyboardType="numeric"
                  placeholder="12"
                />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
            </View>

            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Years Until Retirement</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.yearsUntilRetirement}
                  onChangeText={(v) => handleInputChange('yearsUntilRetirement', v)}
                  keyboardType="numeric"
                  placeholder="30"
                />
                <Text style={styles.suffixText}>Years</Text>
              </View>
            </View>
          </View>

          <View style={[styles.inputRow, isMobile && { flexDirection: 'column', gap: 0 }]}>
            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Current Savings</Text>
              <View style={styles.inputContainer}>
                <Text style={styles.currencySymbol}>₹</Text>
                <TextInput
                  style={styles.input}
                  value={formData.currentSavings}
                  onChangeText={(v) => handleInputChange('currentSavings', v)}
                  keyboardType="numeric"
                  placeholder="0"
                />
              </View>
            </View>

            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Annuity Purchase (%)</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.annuityPurchasePercent}
                  onChangeText={(v) => handleInputChange('annuityPurchasePercent', v)}
                  keyboardType="numeric"
                  placeholder="40"
                />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
              <Text style={styles.helperText}>Typical range 40% to 80% based on retirement plan</Text>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Annuity Return (p.a.)</Text>
            <View style={styles.inputContainer}>
              <TextInput
                style={styles.input}
                value={formData.annuityReturnRate}
                onChangeText={(v) => handleInputChange('annuityReturnRate', v)}
                keyboardType="numeric"
                placeholder="6"
              />
              <Text style={styles.percentSymbol}>%</Text>
            </View>
            <Text style={styles.helperText}>Used to estimate monthly pension payout</Text>
          </View>
        </View>

        <View style={[styles.summaryPanel, isMobile && { flex: 1, marginTop: 16 }]}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Retirement Summary</Text>
            <Text style={styles.panelSubtitle}>Projection Results</Text>
          </View>

          {results ? (
            <View style={styles.resultsContainer}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Retirement Corpus</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.retirementCorpus)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Invested Amount</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.investedAmount)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Expected Returns</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.expectedReturns)}</Text>
              </View>
              <View style={styles.divider} />
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Lump Sum Withdrawal</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.lumpSumWithdrawal)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Annuity Purchase</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.annuityPurchase)}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Est. Monthly Pension</Text>
                <Text style={styles.totalValue}>{formatCurrency(results.estimatedMonthlyPension)}</Text>
              </View>
              {renderChart()}
            </View>
          ) : (
            <Text style={styles.placeholderText}>Enter valid inputs to view results</Text>
          )}
        </View>
      </View>
    </View>
  );

  const renderChart = () => {
    if (!results) return null;
    const chartWidth = isMobile ? Math.min(screenData.width - 40, 320) : 320;
    const data = [
      {
        name: 'Annuity',
        population: Math.round(results.annuityPurchase),
        color: '#2563EB',
        legendFontColor: '#0f172a',
        legendFontSize: 12,
      },
      {
        name: 'Lump Sum',
        population: Math.round(results.lumpSumWithdrawal),
        color: '#10B981',
        legendFontColor: '#0f172a',
        legendFontSize: 12,
      },
    ];
    return (
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Corpus Split at Retirement</Text>
        <PieChart
          data={data}
          width={chartWidth}
          height={180}
          chartConfig={{
            color: () => '#0ea5e9',
          }}
          accessor={'population'}
          backgroundColor={'transparent'}
          paddingLeft={'15'}
          absolute
        />
      </View>
    );
  };

  const renderInfo = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>How this calculator works</Text>
      <Text style={styles.sectionText}>
        We combine the future value of monthly contributions (SIP) with any existing savings to project your
        retirement corpus. At retirement, a portion of the corpus is used to purchase an annuity to estimate
        monthly pension, while the remaining can be taken as a lump-sum.
      </Text>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons name={showFAQ ? 'chevron-up' : 'chevron-down'} size={20} color="#1f2937" />
      </TouchableOpacity>
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is a safe annuity purchase percentage?</Text>
            <Text style={styles.faqAnswer}>
              Many retirement plans suggest purchasing 40% to 80% of the corpus as annuity. You can adjust this
              to match your preference.
            </Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I include a lumpsum I already have?</Text>
            <Text style={styles.faqAnswer}>
              Yes. Enter your current savings and we project its future value to add to your retirement corpus.
            </Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What inflation should I assume?</Text>
            <Text style={styles.faqAnswer}>Long-term inflation in India has ranged 4%–6% historically. For conservative planning, use 5% unless your lifestyle costs rise faster.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How is monthly pension estimated?</Text>
            <Text style={styles.faqAnswer}>We take the annuity purchase portion and apply your annuity return assumption to estimate monthly payout. Actual annuity rates vary by product and age.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How often should I increase my SIP?</Text>
            <Text style={styles.faqAnswer}>A yearly step-up aligned to salary hikes (5%–10%) can materially improve the corpus without straining cashflows.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I retire earlier than the target age?</Text>
            <Text style={styles.faqAnswer}>Yes, but your corpus and pension may be lower. Test scenarios by reducing years of contribution and adjusting annuity rate for realistic expectations.</Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderSEO = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Retirement Calculator: Build a Reliable Pension</Text>
      <Text style={styles.sectionText}>This calculator projects your retirement corpus using monthly contributions (SIP), existing savings, expected returns and years to retirement. We then split the corpus into lump-sum withdrawal and annuity purchase to estimate monthly pension. Use conservative return assumptions and revisit the plan annually.</Text>

      <Text style={styles.seoSubtitle}>Key assumptions and methodology</Text>
      <Text style={styles.sectionText}>• Monthly contributions are compounded • Existing savings grow at the same rate • At retirement, a portion is used to buy annuity • Pension depends on annuity rate and purchase amount • Results are indicative and for planning purposes</Text>

      <Text style={styles.seoSubtitle}>Tips to improve your retirement corpus</Text>
      <Text style={styles.sectionText}>• Start early and invest consistently • Step up SIP yearly with income • Increase tenure where possible • Keep equity allocation disciplined • Avoid frequent withdrawals • Track inflation and adjust targets</Text>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Related calculators</Text>
        <View style={styles.linksRow}>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('NPSCalculator')}>
            <Text style={styles.linkText}>NPS Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SIPCalculator')}>
            <Text style={styles.linkText}>SIP Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('StepUpSIPCalculator')}>
            <Text style={styles.linkText}>Step-Up SIP</Text>
          </TouchableOpacity>
        </View>
      </View>
      <Text style={styles.disclaimer}>Investments are subject to market risks. Use conservative inputs for planning.</Text>
    </View>
  );

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      {renderCalculator()}
      {renderInfo()}
      {renderFAQ()}
      {renderSEO()}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  heroSection: {
    backgroundColor: '#0ea5e9',
    paddingVertical: 24,
    paddingHorizontal: 30,
  },
  heroContent: {
    width: '100%',
    alignSelf: 'stretch',
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6,
  },
  heroSubtitle: {
    fontSize: 14,
    color: '#e0f2fe',
  },
  calculatorContainer: {
    width: '100%',
    alignSelf: 'stretch',
    paddingHorizontal: 30,
    paddingVertical: 20,
  },
  calculatorRow: {
    flexDirection: 'row',
    gap: 24,
  },
  formContent: {
    gap: 20,
    flex: 0.7,
  },
  summaryPanel: {
    flex: 0.3,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    height: 'fit-content',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  panelHeader: {
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  panelTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
  },
  panelSubtitle: {
    fontSize: 12,
    color: '#64748b',
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 16,
  },
  half: {
    flex: 1,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  helperText: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 6,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 12,
    height: 48,
  },
  currencySymbol: {
    fontSize: 16,
    color: '#64748b',
    marginRight: 8,
  },
  percentSymbol: {
    fontSize: 16,
    color: '#64748b',
    marginLeft: 8,
  },
  suffixText: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 8,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
    borderWidth: 0,
  },
  resultsContainer: {
    gap: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: 14,
    color: '#334155',
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  divider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: 8,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#065f46',
  },
  placeholderText: {
    fontSize: 14,
    color: '#64748b',
  },
  infoSection: {
    marginVertical: 20,
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
  },
  sectionText: {
    fontSize: 16,
    color: '#475569',
    lineHeight: 24,
    marginBottom: 12,
  },
  faqContainer: {
    marginBottom: 20,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#0ea5e910',
    borderRadius: 12,
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  faqContent: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  faqItem: {
    marginBottom: 16,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 6,
  },
  faqAnswer: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
  },
  chartSection: {
    marginTop: 12,
    alignItems: 'center',
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  seoContainer: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    marginBottom: 20,
  },
  seoTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
    textAlign: 'center',
  },
  seoSection: {
    marginTop: 8,
    marginBottom: 16,
  },
  seoSubtitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  linksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  linkButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#0ea5e910',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  linkText: {
    color: '#0ea5e9',
    fontWeight: '600',
  },
  disclaimer: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4,
  },
});

export default RetirementCalculatorScreen;